import { Bell, X } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { InstantNotificationTab } from "@/features/user-setting/components/InstantNotificationTab";
import { PeriodicNotificationTab } from "@/features/user-setting/components/PeriodicNotificationTab";

interface EmailNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EmailNotificationModal: React.FC<EmailNotificationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"instantNotifications" | "periodicNotifications">(
    "instantNotifications",
  );

  if (!isOpen) return null;

  const tabs = [
    { key: "instantNotifications", label: t("common.instantNotifications") },
    { key: "periodicNotifications", label: t("common.periodicNotifications") },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b p-6">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <h2 className="font-semibold text-xl">{t("common.emailNotification")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 overflow-x-auto border-b">
          {tabs.map((tab) => (
            <div key={tab.key} className="flex shrink-0 items-center">
              <button
                type="button"
                className={`whitespace-nowrap px-6 py-3 font-medium transition-colors ${
                  activeTab === tab.key
                    ? "border-blue-600 border-b-2 text-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
              >
                {tab.label}
              </button>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {activeTab === "instantNotifications" && <InstantNotificationTab onClose={onClose} />}
              {activeTab === "periodicNotifications" && (
                <PeriodicNotificationTab onClose={onClose} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
